import {
  ActionButtonProps,
  BottomAction,
} from '@euler/app/components/BottomAction';
import { LaunchScreenLikePageContainer } from '@euler/app/components/LaunchScreenLikePageContainer';
import { Typography } from '@euler/app/components/typography';
import { useReportHeaderMetrics } from '@euler/app/flows/report/functions/useReportHeaderMetrics';
import { VibrancyView } from '@euler/components/BlurView';
import { Colors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useOnNavigateBack } from '@euler/functions/useOnNavigateBack';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { MotiView } from 'moti';
import { ReactNode, createContext, memo, useMemo, useState } from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Slot } from 'react-slot-fill';
import { useTheme } from 'styled-components';

export const IPadPageContainerContext = createContext<{
  footerHeight: number | undefined;
}>(null as any);

export const PageContainerForIpad = memo(
  ({
    children,
    name,
    title,
    subTitle,
    actions,
    footer,
    hideHeader,
    titleContainerStyle,
    headerLeftContainerStyle,
    headerRightContainerStyle,
    contentContainerStyle,
    titleStyle,
    headerStyle,
    footerStyle,
  }: {
    title: string;
    subTitle?: string;
    name?: string;
    actions?: ActionButtonProps[];
    children?: ReactNode;
    hideHeader?: boolean;
    footer?: 'default' | 'floating' | 'hidden';
    titleContainerStyle?: StyleProp<ViewStyle>;
    contentContainerStyle?: StyleProp<ViewStyle>;
    headerLeftContainerStyle?: StyleProp<ViewStyle>;
    headerRightContainerStyle?: StyleProp<ViewStyle>;
    headerStyle?: StyleProp<ViewStyle>;
    footerStyle?: StyleProp<ViewStyle>;
    titleStyle?: StyleProp<TextStyle>;
  }) => {
    const { top, bottom } = useSafeAreaInsets();
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const { headerHeight } = useReportHeaderMetrics();
    const bgColor = theme.launchScreen.bgColor;
    const navigation = useNavigation<StackNavigationProp<any>>();

    const onBackPress = useOnNavigateBack(() => {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.replace('Home', {});
      }
    });

    const colors = !hideHeader
      ? Array.isArray(bgColor)
        ? bgColor
        : [bgColor, bgColor]
      : ['transparent', 'transparent'];
    const [footerHeight, setFooterHeight] = useState<number>();
    const values = useMemo(() => ({ footerHeight }), [footerHeight]);
    return (
      <IPadPageContainerContext.Provider value={values}>
        <LaunchScreenLikePageContainer
          transparent={hideHeader}
          css={`
            padding-top: ${top}px;
            padding-bottom: ${bottom}px;
          `}
        >
          {!hideHeader ? (
            <MotiView
              from={{ opacity: 0, translateY: rpx(-200) }}
              animate={{ opacity: 1, translateY: 0 }}
              transition={{ type: 'timing', duration: 500 }}
              css={`
                padding: ${rpx(20)}px ${rpx(32)}px;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                flex-wrap: nowrap;
              `}
              style={headerStyle}
            >
              <View
                css={`
                  flex: 1;
                  flex-direction: row;
                  justify-content: flex-start;
                  align-items: center;
                  overflow: hidden;
                `}
              >
                <TouchableOpacity
                  css={`
                    flex-direction: row;
                    align-items: center;
                    justify-content: flex-start;
                  `}
                  style={titleContainerStyle ?? { flex: 1 }}
                  onPress={onBackPress}
                >
                  <Icon
                    name="arrow-back-circle-outline"
                    size={rpx(60)}
                    color="white"
                  />
                  <Typography.Label
                    bold
                    size={rpx(48)}
                    color={Colors.White}
                    numberOfLines={1}
                    style={titleStyle}
                    css={`
                      margin-left: ${rpx(20)}px;
                    `}
                  >
                    {title ?? ''}
                  </Typography.Label>
                  {subTitle ? (
                    <View
                      css={`
                        flex: 1;
                        justify-content: center;
                        margin-left: ${rpx(10)}px;
                      `}
                    >
                      <Typography.Label
                        thin
                        size={rpx(44)}
                        color={Colors.White5}
                        allowFontScaling
                        adjustsFontSizeToFit
                        numberOfLines={1}
                        lineHeight="auto"
                        style={{
                          textAlignVertical: 'center',
                        }}
                      >
                        {subTitle}
                      </Typography.Label>
                    </View>
                  ) : undefined}
                </TouchableOpacity>
                <View
                  css={`
                    padding: 0 ${rpx(20)}px;
                    flex-direction: row;
                    align-items: center;
                  `}
                  style={headerLeftContainerStyle}
                >
                  <Slot name={iPadPageSlot(name, 'header/left')} />
                </View>
              </View>
              <View
                css={`
                  flex-direction: row;
                  justify-content: flex-end;
                  align-items: center;
                  margin-left: ${rpx(20)}px;
                `}
                style={headerRightContainerStyle}
              >
                <Slot name={iPadPageSlot(name, 'header/right')} />
              </View>
            </MotiView>
          ) : (
            <View
              css={`
                height: ${headerHeight + 32}px;
              `}
            />
          )}
          <LayoutProviderView
            css={`
              flex: 1;
              padding: ${rpx(20)}px;
            `}
            style={contentContainerStyle}
          >
            {children}
          </LayoutProviderView>
          {footer === 'floating' ? (
            <MotiView
              from={{ opacity: 0, translateY: rpx(200) }}
              animate={{ opacity: 1, translateY: 0 }}
              transition={{ type: 'timing', duration: 500 }}
              onLayout={e => setFooterHeight(e.nativeEvent.layout.height)}
              css={`
                position: absolute;
                left: 0;
                right: 0;
                bottom: 0;
              `}
              style={[
                Platform.OS === 'web'
                  ? {
                      height: 100,
                    }
                  : undefined,
                footerStyle,
              ]}
            >
              <View
                style={StyleSheet.absoluteFill}
                css={`
                  background-color: rgba(${colors[0]}, 0.01);
                `}
              />
              <VibrancyView
                blurType="dark"
                blurAmount={100}
                css={`
                  flex: 1;
                `}
              >
                <View
                  css={`
                    padding: ${rpx(32)}px ${rpx(40)}px;
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                  `}
                >
                  <View>
                    <Slot name={iPadPageSlot(name, 'footer/left')} />
                  </View>
                  {actions != null && actions.length > 0 && (
                    <BottomAction inline>
                      {actions.map((action, i) => (
                        <BottomAction.Button {...action} key={i} />
                      ))}
                    </BottomAction>
                  )}
                </View>
              </VibrancyView>
            </MotiView>
          ) : footer !== 'hidden' ? (
            <MotiView
              from={{ opacity: 0, translateY: rpx(200) }}
              animate={{ opacity: 1, translateY: 0 }}
              transition={{ type: 'timing', duration: 500 }}
              onLayout={e => setFooterHeight(e.nativeEvent.layout.height)}
              css={`
                padding: ${rpx(32)}px ${rpx(40)}px;
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
              `}
              style={footerStyle}
            >
              <View>
                <Slot name={iPadPageSlot(name, 'footer/left')} />
              </View>
              {actions != null && actions.length > 0 && (
                <BottomAction inline>
                  {actions.map((action, i) => (
                    <BottomAction.Button {...action} key={i} />
                  ))}
                </BottomAction>
              )}
            </MotiView>
          ) : (
            <View
              css={`
                height: ${rpx(15)}px;
              `}
            />
          )}
        </LaunchScreenLikePageContainer>
      </IPadPageContainerContext.Provider>
    );
  },
);

export function iPadPageSlot(
  pageId: string | undefined,
  slot: 'footer/left' | 'header/left' | 'header/right',
) {
  return `${pageId ? pageId + '/' : ''}page-container/${slot}`;
}
